import { Injectable, inject } from "@angular/core";
import {
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  Timestamp,
  Firestore,
  writeBatch,
  increment,
  QueryConstraint,
} from "@angular/fire/firestore";
import { FirestoreService } from "./firestore.service";
import { Bill, COL, Payment } from "./types";

export interface CreatePaymentInput {
  billId: string;
  mount: number;
  notes?: string;
  electronicBill?: string;
  /**
   * Pre-uploaded receipt URL/name. The modal handles the Storage upload and
   * passes the resulting download URL down so the service stays focused on
   * Firestore writes.
   */
  fileUrl?: string | null;
  fileName?: string | null;
}

/** Firestore caps a single batch at 500 ops. We keep some headroom. */
const BATCH_OP_LIMIT = 450;

/**
 * Cap for the payment history of a single bill. Bills rarely have more than
 * a handful of partial payments; the cap protects against runaway reads.
 */
const PAYMENTS_PER_BILL_CAP = 200;

@Injectable({ providedIn: "root" })
export class PaymentsService {
  private readonly fs = inject(FirestoreService);
  private readonly db = inject(Firestore);

  // ---- Reads ---------------------------------------------------------------

  /** Payments for one bill (newest first). */
  async findByBill(
    billId: string,
    opts?: { limit?: number }
  ): Promise<Payment[]> {
    const ref = this.fs.col<Payment>(COL.payments);
    const constraints: QueryConstraint[] = [];
    constraints.push(where("billId", "==", billId));
    constraints.push(orderBy("createdAt", "desc"));
    constraints.push(fsLimit(opts?.limit ?? PAYMENTS_PER_BILL_CAP));
    const snap = await getDocs(query(ref, ...constraints));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Payment));
  }

  // ---- Writes --------------------------------------------------------------

  /**
   * Single-bill payment. Atomic: bill update, payment creation and the
   * client's denormalized totals all happen in a single batch using
   * `FieldValue.increment`. No read-then-write round-trip on the client.
   */
  async pay(
    input: CreatePaymentInput,
    creator: { uid: string; name: string }
  ): Promise<Payment> {
    const billRef = this.fs.docRef<Bill>(COL.bills, input.billId);
    const billSnap = await getDoc(billRef);
    if (!billSnap.exists()) throw new Error("Factura no encontrada");
    const bill = billSnap.data() as Bill;

    const fileUrl = input.fileUrl ?? null;
    const fileName = input.fileName ?? null;

    const id = doc(this.fs.col(COL.payments)).id;
    const ref = this.fs.docRef<Payment>(COL.payments, id);

    const mount = Number(input.mount);
    const newBalance = Math.max(0, Number(bill.balance) - mount);
    const fullyPaid = newBalance <= 0;
    const wasUnpaid = bill.paid === 0;

    const body: Omit<Payment, "id"> = {
      billId: input.billId,
      clientId: bill.clientId,
      creatorUid: creator.uid,
      creatorName: creator.name,
      mount,
      notes: input.notes ?? null,
      fileUrl,
      fileName,
      electronicBill: input.electronicBill ?? null,
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    };

    const batch = writeBatch(this.db);
    batch.set(ref as any, body as any);
    batch.update(billRef as any, {
      balance: newBalance,
      paid: fullyPaid ? 1 : 0,
      updatedAt: serverTimestamp(),
    });
    batch.update(this.fs.docRef(COL.clients, bill.clientId) as any, {
      totalBalance: increment(-mount),
      unpaidCount: fullyPaid && wasUnpaid ? increment(-1) : increment(0),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();

    const localNow = Timestamp.now();
    return { id, ...body, createdAt: localNow, updatedAt: localNow };
  }

  /**
   * Pays every unpaid bill of a client at once. Reads bills in pages of
   * `billsPerBatch` and commits each page in a single Firestore batch so the
   * memory footprint stays small even for clients with thousands of unpaid
   * bills. Uses `increment` so the client totals are kept consistent
   * batch-by-batch instead of needing a separate read-then-write afterwards.
   */
  async payAllForClient(
    input: {
      clientId: string;
      electronicBill?: string;
      notes?: string;
      fileUrl?: string | null;
      fileName?: string | null;
    },
    creator: { uid: string; name: string }
  ): Promise<{ count: number; total: number }> {
    const billsCol = this.fs.col<Bill>(COL.bills);
    const fileUrl = input.fileUrl || null;
    const fileName = input.fileName || null;
    const clientRef = this.fs.docRef(COL.clients, input.clientId);

    // Each bill consumes 2 ops (payment.set + bill.update). Plus 1 op for
    // the client update once per batch. Reserve room before splitting.
    const billsPerBatch = Math.floor((BATCH_OP_LIMIT - 1) / 2);

    let total = 0;
    let count = 0;
    let cursor: Timestamp | null = null;

    // Defensive ceiling: 200 batches × 224 bills per batch ≈ 44k bills max,
    // well above any realistic per-client load. If we ever hit this we
    // surface it loudly rather than silently capping.
    const MAX_BATCHES = 200;

    for (let batchIdx = 0; batchIdx < MAX_BATCHES; batchIdx += 1) {
      const constraints: QueryConstraint[] = [
        where("clientId", "==", input.clientId),
        where("paid", "==", 0),
        orderBy("createdAt", "desc"),
      ];
      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(fsLimit(billsPerBatch));

      const pageSnap = await getDocs(query(billsCol, ...constraints));
      if (pageSnap.empty) break;

      const batch = writeBatch(this.db);
      let sliceTotal = 0;
      let sliceCount = 0;

      for (const billDoc of pageSnap.docs) {
        const bill = billDoc.data() as Bill;
        const remaining = Number(bill.balance);
        if (remaining <= 0) continue;
        sliceTotal += remaining;
        sliceCount += 1;

        const payId = doc(this.fs.col(COL.payments)).id;
        const payRef = this.fs.docRef<Payment>(COL.payments, payId);

        const payBody: Omit<Payment, "id"> = {
          billId: billDoc.id,
          clientId: input.clientId,
          creatorUid: creator.uid,
          creatorName: creator.name,
          mount: remaining,
          notes: input.notes ?? null,
          fileUrl,
          fileName,
          electronicBill: input.electronicBill ?? null,
          createdAt: serverTimestamp() as unknown as Timestamp,
          updatedAt: serverTimestamp() as unknown as Timestamp,
        };
        batch.set(payRef as any, payBody as any);
        batch.update(billDoc.ref as any, {
          balance: 0,
          paid: 1,
          updatedAt: serverTimestamp(),
        });
      }

      if (sliceCount > 0) {
        batch.update(clientRef as any, {
          totalBalance: increment(-sliceTotal),
          unpaidCount: increment(-sliceCount),
          updatedAt: serverTimestamp(),
        });
        await batch.commit();
        total += sliceTotal;
        count += sliceCount;
      }

      // If this page wasn't full, we're done. Otherwise advance the cursor
      // to the last bill we just paid and read the next page.
      if (pageSnap.docs.length < billsPerBatch) break;
      const lastDoc = pageSnap.docs[pageSnap.docs.length - 1];
      const lastCreatedAt = (lastDoc.data() as Bill).createdAt;
      cursor = lastCreatedAt instanceof Timestamp ? lastCreatedAt : null;
      if (!cursor) break;

      if (batchIdx === MAX_BATCHES - 1) {
        console.warn(
          "payAllForClient hit MAX_BATCHES; some unpaid bills may remain."
        );
      }
    }

    return { count, total };
  }
}
